import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import {Newspaper, Image, Users, TrendingUp, Plus, ArrowRight, BarChart2, Flame, Eye, ExternalLink, Heart, Share2, Trophy, ChevronLeft, ChevronRight} from 'lucide-react'
import type { NewsArticle, Banner, UserProfile, PageView } from '@/types'
import AccessChart from '@/components/AccessChart'
import { shiftDateKey, sumViewsByDateKey, toDateKey } from '@/lib/trackPageView'
import { formatCount, getDisplayedLikes, getDisplayedShares } from '@/lib/engagement'

interface AccessDay {
  label: string
  value: number
  date: Date
  isSpike: boolean
}

function parseDateKey(key: string) {
  const [year, month, day] = key.split('-').map(Number)
  return new Date(year, (month || 1) - 1, day || 1)
}

function mondayOfWeek(dateKey: string) {
  const date = parseDateKey(dateKey)
  const weekday = date.getDay()
  const offset = weekday === 0 ? -6 : 1 - weekday
  date.setDate(date.getDate() + offset)
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function formatWeekRange(startKey: string) {
  const start = parseDateKey(startKey)
  const end = parseDateKey(shiftDateKey(startKey, 6))
  const opts: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short' }
  return `${start.toLocaleDateString('pt-PT', opts)} – ${end.toLocaleDateString('pt-PT', { ...opts, year: 'numeric' })}`
}

function buildRealSeries(totalDays: number, records: PageView[]): AccessDay[] {
  const byDay = sumViewsByDateKey(records)

  const history: AccessDay[] = []
  const todayKey = toDateKey()
  const values: number[] = []

  for (let i = totalDays - 1; i >= 0; i--) {
    const key = shiftDateKey(todayKey, -i)
    const d = parseDateKey(key)
    const value = byDay.get(key) || 0
    values.push(value)
    history.push({
      label: d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' }),
      value,
      date: d,
      isSpike: false,
    })
  }

  const avg = values.reduce((sum, n) => sum + n, 0) / Math.max(values.length, 1)
  return history.map(day => ({
    ...day,
    isSpike: avg > 0 && day.value >= avg * 1.6 && day.value > 0,
  }))
}

export default function DashboardHome() {
  const [stats, setStats] = useState({ articles: 0, published: 0, banners: 0, users: 0 })
  const [recentArticles, setRecentArticles] = useState<NewsArticle[]>([])
  const [allArticles, setAllArticles] = useState<NewsArticle[]>([])
  const [pageViews, setPageViews] = useState<PageView[]>([])
  const [loading, setLoading] = useState(true)
  const [chartDays, setChartDays] = useState<7 | 14 | 30>(14)
  const [gaId, setGaId] = useState('')
  const [weekOffset, setWeekOffset] = useState(0)

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const [articlesRes, bannersRes, usersRes, viewsRes, gaRes] = await Promise.all([
          lumi.entities.news_articles.list({ sort: { createdAt: -1 }, limit: 200 }),
          lumi.entities.banners.list({ filter: { isActive: true }, limit: 100 }),
          lumi.entities.user_profiles.list({ limit: 100 }),
          lumi.entities.page_views.list({
            sort: { dateKey: -1 },
            limit: 90,
          }),
          lumi.entities.site_settings.list({ filter: { key: 'ga_measurement_id' }, limit: 1 }),
        ])
        const allArticles = articlesRes.list as NewsArticle[]
        setStats({
          articles: allArticles.length,
          published: allArticles.filter(a => a.status === 'published').length,
          banners: (bannersRes.list as Banner[]).length,
          users: (usersRes.list as UserProfile[]).length,
        })
        setAllArticles(allArticles)
        setRecentArticles(allArticles.slice(0, 5))
        setPageViews((viewsRes.list || []) as PageView[])
        setGaId(((gaRes.list?.[0] as { value?: string } | undefined)?.value || '').trim())
      } catch (e) {
        console.error('Erro ao carregar estatísticas:', e)
      } finally {
        setLoading(false)
      }
    }
    fetchStats()
  }, [])

  const fullSeries = useMemo(() => buildRealSeries(60, pageViews), [pageViews])
  const accessHistory = fullSeries.slice(60 - chartDays)
  const previousPeriod = fullSeries.slice(60 - chartDays * 2, 60 - chartDays)

  const totalAccesses = accessHistory.reduce((s, d) => s + d.value, 0)
  const avgAccesses = Math.round(totalAccesses / chartDays)
  const prevTotal = previousPeriod.reduce((s, d) => s + d.value, 0)
  const prevAvg = previousPeriod.length > 0 ? Math.round(prevTotal / previousPeriod.length) : 0
  const growthPct = prevAvg > 0 ? Math.round(((avgAccesses - prevAvg) / prevAvg) * 100) : (avgAccesses > 0 ? 100 : 0)

  const todayValue = accessHistory[accessHistory.length - 1]?.value ?? 0
  const peakDay = accessHistory.reduce((best, d) => (d.value > best.value ? d : best), accessHistory[0])
  const hasAnyViews = pageViews.some(item => (item.views || 0) > 0)

  const periodLabel = chartDays === 7 ? 'semana' : chartDays === 14 ? 'quinzena' : 'mês'

  const currentWeekStart = useMemo(() => mondayOfWeek(toDateKey()), [])
  const selectedWeekStart = useMemo(
    () => shiftDateKey(currentWeekStart, weekOffset * 7),
    [currentWeekStart, weekOffset],
  )
  const selectedWeekEnd = useMemo(() => shiftDateKey(selectedWeekStart, 6), [selectedWeekStart])
  const isCurrentWeek = weekOffset === 0

  const topArticles = useMemo(() => {
    const fromTime = parseDateKey(selectedWeekStart).setHours(0, 0, 0, 0)
    const toTime = parseDateKey(selectedWeekEnd).setHours(23, 59, 59, 999)

    return [...allArticles]
      .filter((article) => {
        const stamp = new Date(article.publishedAt || article.createdAt || 0).getTime()
        if (!Number.isFinite(stamp) || stamp <= 0) return false
        if (stamp < fromTime) return false
        if (stamp > toTime) return false
        return true
      })
      .map((article) => {
        const startIso = article.publishedAt || article.createdAt
        const likes = getDisplayedLikes(article.likes, startIso)
        const shares = getDisplayedShares(article.shares, startIso)
        const views = Math.max(0, article.views || 0)
        return {
          article,
          likes,
          shares,
          views,
          score: likes + shares + views,
          publicPath: `/artigo/${encodeURIComponent(article.slug || article._id)}`,
        }
      })
      .sort((a, b) => {
        if (b.score !== a.score) return b.score - a.score
        if (b.views !== a.views) return b.views - a.views
        if (b.likes !== a.likes) return b.likes - a.likes
        if (b.shares !== a.shares) return b.shares - a.shares
        return (b.article.createdAt || '').localeCompare(a.article.createdAt || '')
      })
      .slice(0, 10)
  }, [allArticles, selectedWeekStart, selectedWeekEnd])

  const topPeriodLabel = formatWeekRange(selectedWeekStart)

  const cards = [
    { label: 'Total de Notícias', value: stats.articles, icon: Newspaper, color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950' },
    { label: 'Publicadas', value: stats.published, icon: TrendingUp, color: 'text-green-600 dark:text-green-400', bg: 'bg-green-50 dark:bg-green-950' },
    { label: 'Banners Ativos', value: stats.banners, icon: Image, color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950' },
    { label: 'Usuários', value: stats.users, icon: Users, color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950' },
  ]

  return (
    <div className="p-4 sm:p-6 max-w-5xl mx-auto">
      <div className="mb-6 sm:mb-8 flex items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Visão Geral</h1>
          <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-1">Bem-vindo ao painel de CARLOS FELIPE</p>
        </div>
        <Link
          to="/admin/nova-noticia"
          className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-red-600 hover:bg-red-700 text-white text-xs sm:text-sm font-semibold rounded-lg transition-colors flex-shrink-0"
        >
          <Plus size={15} />
          <span className="hidden sm:inline">Nova Notícia</span>
          <span className="sm:hidden">Nova</span>
        </Link>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        {cards.map(card => {
          const Icon = card.icon
          return (
            <div key={card.label} className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 p-4 sm:p-5">
              <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-lg ${card.bg} flex items-center justify-center mb-3`}>
                <Icon size={18} className={card.color} />
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                {loading ? '—' : card.value}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">{card.label}</p>
            </div>
          )
        })}
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 mb-6 sm:mb-8">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <BarChart2 size={16} className="text-red-600 dark:text-red-400" />
            <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Acessos Diários</h2>
          </div>
          <div className="flex items-center gap-1">
            {([7, 14, 30] as const).map(d => (
              <button
                key={d}
                onClick={() => setChartDays(d)}
                className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                  chartDays === d
                    ? 'bg-red-600 text-white'
                    : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
                }`}
              >
                {d}d
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-gray-100 dark:divide-gray-800 border-b border-gray-100 dark:border-gray-800">
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{loading ? '—' : todayValue.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-gray-400 mt-0.5">Hoje</p>
          </div>
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{loading ? '—' : avgAccesses.toLocaleString('pt-BR')}</p>
            <p className="text-xs text-gray-400 mt-0.5">Média/dia</p>
          </div>
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white flex items-center justify-center gap-1">
              {peakDay?.isSpike && <Flame size={14} className="text-orange-500" />}
              {loading ? '—' : (peakDay?.value ?? 0).toLocaleString('pt-BR')}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">Pico ({peakDay?.label})</p>
          </div>
          <div className="px-4 sm:px-5 py-3 text-center">
            <p className={`text-lg sm:text-xl font-bold ${growthPct >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
              {loading ? '—' : `${growthPct >= 0 ? '+' : ''}${growthPct}%`}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">vs. {periodLabel} anterior</p>
          </div>
        </div>

        <div className="px-4 sm:px-5 py-4">
          {loading ? (
            <div className="h-52 flex items-center justify-center text-sm text-gray-400">A carregar...</div>
          ) : !hasAnyViews ? (
            <div className="h-52 flex flex-col items-center justify-center gap-2 text-center px-4">
              <div className="w-10 h-10 rounded-xl bg-red-50 dark:bg-red-950/40 flex items-center justify-center">
                <Eye size={18} className="text-red-600" />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Ainda sem visualizações reais</p>
              <p className="text-xs text-gray-400 max-w-sm">
                Cada visita à página inicial ou a uma matéria passa a ser contada. Atualize o site público e volte aqui para ver o aumento.
              </p>
            </div>
          ) : (
            <AccessChart data={accessHistory} />
          )}
          <p className="text-xs text-gray-300 dark:text-gray-700 mt-3 text-right select-none">
            Visualizações reais no site · CARLOS FELIPE Analytics
          </p>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 mb-6 sm:mb-8 p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-gray-900 dark:text-white">Google Analytics 4</p>
            {gaId ? (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ligado com <span className="font-mono text-gray-800 dark:text-gray-200">{gaId}</span>. Sessões, origem, dispositivo e páginas mais vistas ficam no relatório oficial do Google.
              </p>
            ) : (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Ainda não configurado. Adicione o ID G- em Rodapé & SEO para medir tráfego real no Google Analytics.
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
              gaId
                ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400'
                : 'bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-400'
            }`}>
              {gaId ? 'Ativo' : 'Pendente'}
            </span>
            {gaId ? (
              <a
                href="https://analytics.google.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-xs font-semibold transition-colors"
              >
                Abrir relatórios <ExternalLink size={12} />
              </a>
            ) : (
              <Link
                to="/admin/rodape"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-900 dark:bg-gray-700 hover:bg-gray-800 text-white text-xs font-semibold transition-colors"
              >
                Configurar
              </Link>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800 mb-6 sm:mb-8">
        <div className="px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0">
              <Trophy size={16} className="text-red-600 dark:text-red-400 flex-shrink-0" />
              <div className="min-w-0">
                <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Top 10 da semana</h2>
                <p className="text-[11px] text-gray-400 mt-0.5">
                  {isCurrentWeek ? 'Semana actual' : 'Semana seleccionada'} · {topPeriodLabel}
                </p>
              </div>
            </div>
            <Link to="/admin/noticias" className="text-xs text-red-600 dark:text-red-400 hover:underline flex items-center gap-1 flex-shrink-0">
              Ver todas <ArrowRight size={12} />
            </Link>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setWeekOffset((prev) => prev - 1)}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              <ChevronLeft size={14} />
              Semana anterior
            </button>
            <button
              type="button"
              onClick={() => setWeekOffset((prev) => prev + 1)}
              disabled={weekOffset >= 0}
              className="inline-flex h-8 items-center gap-1 rounded-lg border border-gray-200 px-2.5 text-xs font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
            >
              Semana seguinte
              <ChevronRight size={14} />
            </button>
            {weekOffset !== 0 ? (
              <button
                type="button"
                onClick={() => setWeekOffset(0)}
                className="h-8 rounded-lg px-2.5 text-xs font-semibold text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40"
              >
                Esta semana
              </button>
            ) : null}
          </div>
        </div>
        <div className="divide-y divide-gray-50 dark:divide-gray-800">
          {loading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">A carregar...</div>
          ) : topArticles.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">
              Nenhuma matéria nesta semana.
            </div>
          ) : topArticles.map((item, index) => {
            const article = item.article
            return (
              <div key={article._id} className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3">
                <span className={`w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold flex-shrink-0 ${
                  index === 0
                    ? 'bg-red-600 text-white'
                    : index === 1
                    ? 'bg-gray-800 dark:bg-gray-200 text-white dark:text-gray-900'
                    : index === 2
                    ? 'bg-amber-500 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300'
                }`}>
                  {index + 1}
                </span>
                <img
                  src={article.imageUrl}
                  alt={article.title}
                  className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100 dark:bg-gray-800"
                />
                <div className="flex-1 min-w-0">
                  <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">{article.title}</p>
                  <div className="flex items-center gap-2 mt-0.5 min-w-0">
                    <p className="text-xs text-gray-400 truncate">{article.authorName || 'CARLOS FELIPE'}</p>
                    <a
                      href={item.publicPath}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-red-600 dark:text-red-400 hover:underline flex-shrink-0"
                    >
                      Link <ExternalLink size={11} />
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0 text-[11px] sm:text-xs font-semibold tabular-nums">
                  <span className="inline-flex items-center gap-1 text-gray-600 dark:text-gray-300" title="Visualizações">
                    <Eye size={13} className="text-gray-400" />
                    {formatCount(item.views)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-rose-600 dark:text-rose-400" title="Curtidas">
                    <Heart size={13} />
                    {formatCount(item.likes)}
                  </span>
                  <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400" title="Partilhas">
                    <Share2 size={13} />
                    {formatCount(item.shares)}
                  </span>
                </div>
              </div>
            )
          })}
        </div>
      </div>

      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-100 dark:border-gray-800">
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-gray-100 dark:border-gray-800">
          <h2 className="font-semibold text-gray-900 dark:text-white text-sm">Últimas Notícias</h2>
          <Link to="/admin/noticias" className="text-xs text-red-600 dark:text-red-400 hover:underline flex items-center gap-1">
            Ver todas <ArrowRight size={12} />
          </Link>
        </div>
        <div className="divide-y divide-gray-50 dark:divide-gray-800">
          {loading ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">A carregar...</div>
          ) : recentArticles.length === 0 ? (
            <div className="px-5 py-8 text-center text-sm text-gray-400">Nenhuma notícia cadastrada ainda.</div>
          ) : recentArticles.map(article => (
            <div key={article._id} className="flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-10 h-10 sm:w-12 sm:h-12 rounded-lg object-cover flex-shrink-0 bg-gray-100 dark:bg-gray-800"
              />
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white truncate">{article.title}</p>
                <p className="text-xs text-gray-400 mt-0.5 truncate">{article.authorName} · {new Date(article.createdAt).toLocaleDateString('pt-BR')}</p>
              </div>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium flex-shrink-0 ${
                article.status === 'published'
                  ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400'
                  : article.status === 'draft'
                  ? 'bg-yellow-50 dark:bg-yellow-950 text-yellow-700 dark:text-yellow-400'
                  : 'bg-gray-100 dark:bg-gray-800 text-gray-500'
              }`}>
                {article.status === 'published' ? 'Publicado' : article.status === 'draft' ? 'Rascunho' : 'Arquivado'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
